/**
 * components/ui/Toast.tsx
 * Lightweight toast notification system.
 * Usage: import { toast } from './Toast'
 *        toast.success('Note pinned!')
 *        toast.error('Something went wrong')
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, AlertCircle, Info } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id:      number;
  message: string;
  type:    ToastType;
}

// ─── Global event bus ─────────────────────────────────────────────────────────
type Listener = (item: ToastItem) => void;
const listeners: Listener[] = [];
let   nextId = 1;

const emit = (type: ToastType, message: string) => {
  const item: ToastItem = { id: nextId++, type, message };
  listeners.forEach(l => l(item));
};

// ─── Public API ───────────────────────────────────────────────────────────────
export const toast = {
  success: (msg: string) => emit('success', msg),
  error:   (msg: string) => emit('error',   msg),
  info:    (msg: string) => emit('info',     msg),
};

// ─── Toast item component ─────────────────────────────────────────────────────
const ToastCard: React.FC<{ item: ToastItem; onRemove: (id: number) => void }> = ({ item, onRemove }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setVisible(true));
    // Auto remove after 3s
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onRemove(item.id), 300);
    }, 3000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  const styles: Record<ToastType, { bg: string; border: string; color: string; icon: React.ReactNode }> = {
    success: { bg: '#F0FDF4', border: '#16A34A33', color: '#16A34A', icon: <Check size={15}/> },
    error:   { bg: '#FEF2F2', border: '#DC262633', color: '#DC2626', icon: <AlertCircle size={15}/> },
    info:    { bg: '#FFF4E0', border: '#F5A62333', color: '#C4811A', icon: <Info size={15}/> },
  };
  const s = styles[item.type];

  return (
    <div style={{
      display:        'flex',
      alignItems:     'center',
      gap:            10,
      padding:        '12px 16px',
      borderRadius:   12,
      background:     s.bg,
      border:         `1px solid ${s.border}`,
      boxShadow:      '0 4px 16px rgba(0,0,0,0.10)',
      fontFamily:     "'DM Sans',sans-serif",
      fontSize:       14,
      fontWeight:     600,
      color:          '#1A1814',
      minWidth:       220,
      maxWidth:       320,
      transform:      visible ? 'translateY(0)' : 'translateY(20px)',
      opacity:        visible ? 1 : 0,
      transition:     'transform 0.25s ease, opacity 0.25s ease',
    }}>
      <span style={{ color: s.color, flexShrink: 0 }}>{s.icon}</span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{item.message}</span>
      <button
        onClick={() => { setVisible(false); setTimeout(() => onRemove(item.id), 300); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7A7570', padding: 2, display: 'flex', flexShrink: 0 }}
      >
        <X size={13}/>
      </button>
    </div>
  );
};

// ─── Toast container ──────────────────────────────────────────────────────────
export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const listener: Listener = (item) => {
      setToasts(prev => [...prev, item]);
    };
    listeners.push(listener);
    return () => {
      const idx = listeners.indexOf(listener);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position:       'fixed',
      bottom:         24,
      right:          24,
      zIndex:         9999,
      display:        'flex',
      flexDirection:  'column',
      gap:            10,
      pointerEvents:  'none',
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{ pointerEvents: 'all' }}>
          <ToastCard item={t} onRemove={remove}/>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;